import {
  Box, Button, ButtonText, HStack, Text, VStack,
} from '@gluestack-ui/themed';
import { useState } from 'react';

import { AIPresenceOrb } from '@/common/components/presence/AIPresenceOrb';
import { computeSpeakingPulseOverhangPx } from '@/common/components/presence/presenceGlowBudget';
import type { AIPresenceState, PresenceSpeechEnvelope } from '@/common/components/presence/types';
import { useFakeSpeechEnvelope } from '@/visual-lab/chapters/orb/useFakeSpeechEnvelope';

const STATES: AIPresenceState[] = ['idle', 'listening', 'thinking', 'speaking'];
const SIZES = [80, 140, 200, 280];
const BACKDROPS = [
  { id: 'light', label: 'Light card', bg: '#ffffff' },
  { id: 'subtle', label: 'Subtle grey', bg: '#f4f6fa' },
  { id: 'dark', label: 'Dark', bg: '#0e1218' },
];

function ControlGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <VStack space="xs">
      <Text size="xs" color="$textSubtle" fontWeight="semibold" letterSpacing={0.4}>
        {label.toUpperCase()}
      </Text>
      <HStack space="xs" flexWrap="wrap">
        {children}
      </HStack>
    </VStack>
  );
}

function ToggleChip({
  active, onPress, label,
}: { active: boolean; onPress: () => void; label: string }) {
  return (
    <Button
      onPress={onPress}
      size="sm"
      variant={active ? 'solid' : 'outline'}
      action="primary"
    >
      <ButtonText>{label}</ButtonText>
    </Button>
  );
}

function LabOrb({
  state,
  size,
  speech,
}: {
  state: AIPresenceState;
  size: number;
  speech?: PresenceSpeechEnvelope;
}) {
  return (
    <AIPresenceOrb
      state={state}
      size={size}
      speechEnvelopeOverride={state === 'speaking' ? speech : undefined}
    />
  );
}

function VoiceMeter({ speech }: { speech: PresenceSpeechEnvelope }) {
  const ampPct = Math.round(speech.amp * 100);
  const punchPct = Math.round(speech.punch * 100);
  return (
    <VStack space="xs" px="$4" pb="$4" width="100%" maxWidth={420} alignSelf="center">
      <Text size="xs" color="$textSubtle" fontWeight="semibold" letterSpacing={0.4}>
        FAKE VOICE ENVELOPE
      </Text>
      <HStack space="sm" alignItems="center">
        <Text size="xs" color="$textBody" width={52}>
          amp
        </Text>
        <Box flex={1} height={8} borderRadius="$full" bg="$bgSubtle" overflow="hidden">
          <Box height="100%" width={`${ampPct}%`} bg="$textBrand" />
        </Box>
        <Text size="xs" color="$textSubtle" width={36} textAlign="right">
          {ampPct}
        </Text>
      </HStack>
      <HStack space="sm" alignItems="center">
        <Text size="xs" color="$textBody" width={52}>
          punch
        </Text>
        <Box flex={1} height={8} borderRadius="$full" bg="$bgSubtle" overflow="hidden">
          <Box height="100%" width={`${punchPct}%`} bg="$textHeading" />
        </Box>
        <Text size="xs" color="$textSubtle" width={36} textAlign="right">
          {punchPct}
        </Text>
      </HStack>
    </VStack>
  );
}

/**
 * Standalone playground for tuning the unified AI presence orb.
 * Renders the orb across all states, sizes, and backdrops in parallel
 * so design iterations are immediately comparable.
 */
export function OrbLabScreen() {
  const [stateOverride, setStateOverride] = useState<AIPresenceState | null>('speaking');
  const [size, setSize] = useState<number>(200);
  const [backdrop, setBackdrop] = useState<typeof BACKDROPS[number]>(BACKDROPS[1]);
  const [fakeVoice, setFakeVoice] = useState(true);
  const focusState = stateOverride ?? 'speaking';
  const speech = useFakeSpeechEnvelope(fakeVoice);
  const speakingPulseRoom = focusState === 'speaking' ? 88 : 32;

  return (
    <Box flex={1} bg="$bgSubtle" width="100%">
      <VStack space="lg" px="$5" py="$6" maxWidth={1200} alignSelf="center" width="100%">
        <VStack space="xs">
          <Text size="2xl" color="$textHeading" fontWeight="semibold">
            Orb Lab
          </Text>
          <Text size="sm" color="$textSubtle">
            Idle is dim — almost off. Listening is the quiet default. Thinking and
            speaking share the same cloud motion; speaking only adds voice amp/punch.
          </Text>
        </VStack>

        {/* Controls */}
        <HStack space="lg" flexWrap="wrap">
          <ControlGroup label="State">
            {(['auto', ...STATES] as Array<'auto' | AIPresenceState>).map((s) => (
              <ToggleChip
                key={s}
                active={stateOverride === s || (s === 'auto' && stateOverride === null)}
                onPress={() => setStateOverride(s === 'auto' ? null : s)}
                label={s}
              />
            ))}
          </ControlGroup>
          <ControlGroup label="Size">
            {SIZES.map((s) => (
              <ToggleChip
                key={s}
                active={size === s}
                onPress={() => setSize(s)}
                label={`${s}px`}
              />
            ))}
          </ControlGroup>
          <ControlGroup label="Backdrop">
            {BACKDROPS.map((b) => (
              <ToggleChip
                key={b.id}
                active={backdrop.id === b.id}
                onPress={() => setBackdrop(b)}
                label={b.label}
              />
            ))}
          </ControlGroup>
          <ControlGroup label="Voice">
            <ToggleChip
              active={fakeVoice}
              onPress={() => setFakeVoice(true)}
              label="fake profile"
            />
            <ToggleChip
              active={!fakeVoice}
              onPress={() => setFakeVoice(false)}
              label="off"
            />
          </ControlGroup>
        </HStack>

        {/* Focus tile: large preview with chosen settings */}
        <Box
          borderRadius="$xl"
          borderWidth={1}
          borderColor="$borderLight"
          overflow="visible"
          bg="$bgApp"
        >
          <Text size="xs" color="$textSubtle" fontWeight="semibold" px="$4" py="$2" letterSpacing={0.4}>
            FOCUS PREVIEW
          </Text>
          <Box
            width="100%"
            py={speakingPulseRoom}
            alignItems="center"
            justifyContent="center"
            overflow="visible"
            style={{ backgroundColor: backdrop.bg }}
          >
            <LabOrb
              state={focusState}
              size={size}
              speech={speech}
            />
          </Box>
          {focusState === 'speaking' && speech ? <VoiceMeter speech={speech} /> : null}
        </Box>

        {/* Grid: all four states side-by-side at chosen size */}
        <Box
          borderRadius="$xl"
          borderWidth={1}
          borderColor="$borderLight"
          overflow="visible"
          bg="$bgApp"
        >
          <Text size="xs" color="$textSubtle" fontWeight="semibold" px="$4" py="$2" letterSpacing={0.4}>
            ALL STATES
          </Text>
          <HStack flexWrap="wrap" justifyContent="space-around" py={speakingPulseRoom} overflow="visible" style={{ backgroundColor: backdrop.bg }}>
            {STATES.map((s) => (
              <VStack key={s} alignItems="center" space="sm" px="$4" py="$2">
                <LabOrb state={s} size={size} speech={speech} />
                <Text size="xs" color="$textBody" fontWeight="medium" mt={computeSpeakingPulseOverhangPx(size)}>
                  {s}
                </Text>
              </VStack>
            ))}
          </HStack>
        </Box>

        {/* Grid: all sizes at chosen state */}
        <Box
          borderRadius="$xl"
          borderWidth={1}
          borderColor="$borderLight"
          overflow="visible"
          bg="$bgApp"
        >
          <Text size="xs" color="$textSubtle" fontWeight="semibold" px="$4" py="$2" letterSpacing={0.4}>
            SIZE LADDER
          </Text>
          <HStack flexWrap="wrap" alignItems="flex-end" justifyContent="space-around" py={speakingPulseRoom} overflow="visible" style={{ backgroundColor: backdrop.bg }}>
            {SIZES.map((s) => (
              <VStack key={s} alignItems="center" space="sm" px="$4" py="$2">
                <LabOrb
                  state={focusState}
                  size={s}
                  speech={speech}
                />
                <Text size="xs" color="$textBody" fontWeight="medium" mt={computeSpeakingPulseOverhangPx(s)}>
                  {s}
                  px
                </Text>
              </VStack>
            ))}
          </HStack>
        </Box>
      </VStack>
    </Box>
  );
}
